import { z } from 'zod';

import { paginated } from '../../api/pagination';
import { DELIVERY_STATUSES, PROVIDER_STATUSES } from '../dispatch/schemas';

/**
 * Events, as the roster API speaks them.
 *
 * THE SERVER COMPUTES THE STATUS, and that is the important thing about this
 * file. `status` on an event and on a shift are derived by
 * `@repo/roster-domain` on the way out, so a badge the app renders cannot
 * disagree with a gate the server enforces. Nothing here recomputes them.
 *
 * `label` comes down as English ("Ready", "Need 3"). Render from `kind` and
 * `need` through `t()` instead — every user-facing string in this repo goes
 * through i18n, and a server-rendered label cannot. `label` is kept because it
 * is what a log or a bug report should quote.
 *
 * Same wire asymmetry as staff: responses are snake_case, request bodies are
 * camelCase. See `apps/api/src/features/events/`.
 */

export const EVENT_STATUS_KINDS = ['cancelled', 'complete', 'no_shifts', 'ready', 'need'] as const;
export type EventStatusKind = (typeof EVENT_STATUS_KINDS)[number];

export const STATUS_TONES = ['confirm', 'attention', 'neutral'] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

/** A reply. `null` is "no answer yet", a third state rather than a default. */
export const ANSWERS = ['yes', 'no'] as const;
export type Answer = (typeof ANSWERS)[number];

const eventStatusSchema = z
  .object({
    kind: z.enum(EVENT_STATUS_KINDS),
    label: z.string(),
    tone: z.enum(STATUS_TONES),
    need: z.number().int().nonnegative(),
  })
  .transform((raw) => ({ kind: raw.kind, label: raw.label, tone: raw.tone, need: raw.need }));

const countsSchema = z
  .object({
    asked: z.number().int().nonnegative(),
    notified: z.number().int().nonnegative(),
    yes: z.number().int().nonnegative(),
    no: z.number().int().nonnegative(),
    awaiting: z.number().int().nonnegative(),
  })
  .transform((raw) => ({
    asked: raw.asked,
    notified: raw.notified,
    yes: raw.yes,
    no: raw.no,
    /**
     * Only people actually MESSAGED can be awaited.
     *
     * Assigned-but-never-sent is a draft, not a pending reply — the server draws
     * that line and the client must not redraw it as `asked - yes - no`.
     */
    awaiting: raw.awaiting,
  }));

/**
 * How far the messages naming an event have got, while any are still going
 * out — one at a time, 20–60 s apart.
 *
 * Counts the messages that name THIS event only, so a send to twelve people
 * reads "1 of 4" on an event four of them are wanted for.
 */
const eventSendingSchema = z.object({
  total: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Queued or sending. Never zero: with nothing waiting, `sending` is null. */
  waiting: z.number().int().nonnegative(),
});

export type EventSending = z.infer<typeof eventSendingSchema>;

export const eventSummarySchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    /** `YYYY-MM-DD`. Compared as a string — ISO dates sort lexicographically. */
    date: z.string(),
    completed: z.boolean(),
    cancelled: z.boolean(),
    shift_count: z.number().int().nonnegative(),
    /** Shifts that are not ready. "Shifts to fill" counts these, not people. */
    unready_shift_count: z.number().int().nonnegative(),
    status: eventStatusSchema,
    counts: countsSchema,
    /**
     * On the LIST row only, and null once every message has gone. Optional so
     * an older server, or the mock, still parses.
     */
    sending: eventSendingSchema.nullable().optional(),
  })
  .transform((raw) => ({
    id: raw.id,
    name: raw.name,
    type: raw.type,
    date: raw.date,
    completed: raw.completed,
    cancelled: raw.cancelled,
    shiftCount: raw.shift_count,
    unreadyShiftCount: raw.unready_shift_count,
    status: raw.status,
    counts: raw.counts,
    sending: raw.sending ?? null,
  }));

export type EventSummary = z.infer<typeof eventSummarySchema>;

const assignmentSchema = z
  .object({
    staff_id: z.string(),
    /**
     * Denormalised by the server, and `null` when the person is gone entirely.
     *
     * Carries `archived` because that is exactly the signal the old local-state
     * UI lacked: a removed person silently vanished from the report instead of
     * being shown as an ex-employee who worked the shift.
     */
    staff: z
      .object({
        id: z.string(),
        name: z.string(),
        job: z.string(),
        archived: z.boolean(),
      })
      .nullable(),
    notified: z.boolean(),
    /**
     * A message naming this assignment is waiting to go. Messages to staff are
     * spaced out and wait while the number is down, so this can last minutes
     * or hours. Before `notified` it means asked-but-not-yet-reached, which is
     * not the same as never being asked; after, it is a REMINDER waiting its
     * turn. Optional so an older server, or the mock, still parses.
     */
    queued: z.boolean().optional(),
    answer: z.enum(ANSWERS).nullable(),
    /** How the reply arrived — a WhatsApp message, or a coordinator marking it. */
    answer_source: z.string().nullable(),
    answered_at: z.iso.datetime().nullable(),
  })
  .transform((raw) => ({
    staffId: raw.staff_id,
    staff: raw.staff,
    notified: raw.notified,
    queued: raw.queued ?? false,
    answer: raw.answer,
    answerSource: raw.answer_source,
    answeredAt: raw.answered_at,
  }));

export type Assignment = z.infer<typeof assignmentSchema>;

const shiftSchema = z
  .object({
    id: z.string(),
    /** 24-hour `HH:mm`. Sorts lexicographically, which is how the day reads. */
    start_time: z.string(),
    min_staff: z.number().int().positive(),
    /**
     * Everyone who said yes works — there is no choosing and no standby — so
     * `yesCount` is the number working, and may be more than `min_staff`.
     */
    status: z
      .object({
        ready: z.boolean(),
        need: z.number().int().nonnegative(),
        yes_count: z.number().int().nonnegative(),
      })
      .transform((raw) => ({
        ready: raw.ready,
        need: raw.need,
        yesCount: raw.yes_count,
      })),
    assignments: z.array(assignmentSchema),
  })
  .transform((raw) => ({
    id: raw.id,
    startTime: raw.start_time,
    minStaff: raw.min_staff,
    status: raw.status,
    assignments: raw.assignments,
  }));

export type Shift = z.infer<typeof shiftSchema>;

/**
 * The latest roster report sent to the manager, or null if none ever was.
 *
 * `status` is the send (queued → sending → sent | failed); `providerStatus` is
 * what WhatsApp reported after it (delivered, read). "Sent" is the server
 * handing it over; "delivered" is the manager's phone having it.
 */
const lastReportSchema = z
  .object({
    created_at: z.string(),
    status: z.enum(DELIVERY_STATUSES),
    sent_at: z.string().nullable(),
    provider_status: z.enum(PROVIDER_STATUSES).nullable(),
    delivered_at: z.string().nullable(),
    read_at: z.string().nullable(),
  })
  .transform((raw) => ({
    createdAt: raw.created_at,
    status: raw.status,
    sentAt: raw.sent_at,
    providerStatus: raw.provider_status,
    deliveredAt: raw.delivered_at,
    readAt: raw.read_at,
  }));

export type LastReport = z.infer<typeof lastReportSchema>;

export const eventDetailSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    date: z.string(),
    completed: z.boolean(),
    cancelled: z.boolean(),
    shift_count: z.number().int().nonnegative(),
    unready_shift_count: z.number().int().nonnegative(),
    status: eventStatusSchema,
    counts: countsSchema,
    shifts: z.array(shiftSchema),
    // Optional so an older server (or the mock) without it still parses.
    last_report: lastReportSchema.nullable().optional(),
  })
  .transform((raw) => ({
    id: raw.id,
    name: raw.name,
    type: raw.type,
    date: raw.date,
    completed: raw.completed,
    cancelled: raw.cancelled,
    shiftCount: raw.shift_count,
    unreadyShiftCount: raw.unready_shift_count,
    status: raw.status,
    counts: raw.counts,
    shifts: raw.shifts,
    lastReport: raw.last_report ?? null,
  }));

export type EventDetail = z.infer<typeof eventDetailSchema>;

export const eventPageSchema = paginated(eventSummarySchema);
export type EventPage = z.infer<typeof eventPageSchema>;

export const eventListParamsSchema = z.object({
  /** `YYYY-MM-DD`, inclusive. The Events screen sends the visible range. */
  from: z.string().optional(),
  to: z.string().optional(),
  status: z.enum(['open', 'complete', 'cancelled', 'all']).default('all'),
});

export type EventListParamsInput = z.input<typeof eventListParamsSchema>;
export type EventListParams = z.infer<typeof eventListParamsSchema>;

/**
 * `YYYY-MM-DD`, and a REAL calendar date rather than just the right shape.
 *
 * `2026-02-31` matches the regex and is not a day. The round-trip through Date
 * is what catches it, and it mirrors the server's own check — the form should
 * never be the looser of the two.
 */
const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Pick a date for the event.')
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, 'Pick a real date.');

/**
 * The only venue type this business runs.
 *
 * A single-member enum rather than free text, because the form now auto-selects
 * it and offers nothing else. Kept as an enum rather than dropped entirely so
 * adding a second type later is one line here plus one chip, and so the value
 * cannot drift to `'stadium'` or `'Stadium '` on the way to a server that
 * accepts any string up to 60 characters.
 */
export const EVENT_TYPES = ['Stadium'] as const;
export type EventType = (typeof EVENT_TYPES)[number];

export const createEventInputSchema = z.object({
  name: z.string().trim().min(1, 'Give the event a name.').max(160),
  type: z.enum(EVENT_TYPES).default('Stadium'),
  date: isoDateSchema,
});

export type CreateEventInput = z.input<typeof createEventInputSchema>;

export const updateEventInputSchema = createEventInputSchema.partial();
export type UpdateEventInput = z.input<typeof updateEventInputSchema>;

/** `HH:mm`, 24-hour. Matches the CHECK on the column. */
const startTimeSchema = z
  .string()
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Enter a start time as HH:mm.');

/**
 * Shifts are created in BATCHES, mirroring the Add-shift screen, which queues
 * several start times and commits them together.
 */
export const addShiftsInputSchema = z.object({
  shifts: z
    .array(
      z.object({
        startTime: startTimeSchema,
        minStaff: z.coerce.number().int().min(1, 'A shift needs at least one person.').max(200),
        staffIds: z.array(z.string().min(1)).default([]),
      }),
    )
    .min(1, 'Add at least one shift.'),
});

export type AddShiftsInput = z.input<typeof addShiftsInputSchema>;

export const updateShiftInputSchema = z.object({
  startTime: startTimeSchema.optional(),
  minStaff: z.coerce.number().int().min(1).max(200).optional(),
});

export type UpdateShiftInput = z.input<typeof updateShiftInputSchema>;

export const assignmentsInputSchema = z.object({
  staffIds: z.array(z.string().min(1)).min(1, 'Pick at least one person.'),
});

export type AssignmentsInput = z.input<typeof assignmentsInputSchema>;

export const responseInputSchema = z.object({
  /** `null` CLEARS the reply. A third state, not a default. */
  answer: z.enum(ANSWERS).nullable(),
});

export type ResponseInput = z.input<typeof responseInputSchema>;
